import React from "react";
import { useState, useEffect } from "react";
import { countBy, groupBy, meanBy, round } from "lodash";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Tooltip,
  LinearProgress,
  useTheme,
} from "@mui/material";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import AssessmentVisualizations from "./AssessmentVisualizations";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
const AssessmentStats = ({
  assessments,
  classId = null,
  styles,
  forStartPage = false,
}) => {
  const [stats, setStats] = useState(null);
  const theme = useTheme();

  const gradeColors = {
    A: "#2E7D32",
    B: "#1976D2",
    C: "#FFA000",
    D: "#ED6C02",
    E: "#D32F2F",
    F: "#757575",
  };

  useEffect(() => {
    if (!assessments?.length) return;

    // Filtrera bort assessments som saknar scores
    const validAssessments = assessments.filter(
      (a) =>
        a?.scores?.overallScore?.grade &&
        a?.scores?.criteriaScores &&
        Object.keys(a.scores.criteriaScores).length > 0,
    );

    if (!validAssessments.length) {
      setStats({
        total: 0,
        gradeDistribution: {},
        criteriaStats: [],
        averageScore: 0,
      });
      return;
    }

    const filteredAssessments = classId
      ? validAssessments.filter((a) => a.classId === classId)
      : validAssessments;

    // Samla ihop alla kriterier för att räkna ut snitt per kriterie
    const allCriteriaScores = filteredAssessments.flatMap((assessment) =>
      Object.entries(assessment.scores.criteriaScores)
        .filter(([_, data]) => data?.score != null && data?.grade)
        .map(([name, data]) => ({
          name,
          score: data.score,
          grade: data.grade,
        })),
    );

    // Gruppera efter kriterienamn för snittberäkning
    const criteriaStats = Object.entries(
      groupBy(allCriteriaScores, "name"),
    ).map(([name, scores]) => ({
      name,
      averageScore: round(meanBy(scores, "score"), 1),
      gradeDistribution: countBy(scores, "grade"),
    }));

    const calculatedStats = {
      total: filteredAssessments.length,
      gradeDistribution: countBy(
        filteredAssessments,
        (a) => a.scores.overallScore.grade,
      ),
      criteriaStats,
      averageScore: round(
        meanBy(filteredAssessments, (a) => a.scores.overallScore.score),
        1,
      ),
    };

    setStats(calculatedStats);
  }, [assessments, classId]);

  if (!stats)
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography>Laddar statistik...</Typography>
      </Box>
    );

  if (stats.total === 0)
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography>Inga bedömningar att visa statistik för.</Typography>
      </Box>
    );

  // Resten av komponenten är oförändrad...
  return (
    <Paper
      elevation={0}
      sx={{
        ...styles?.freshBox,
        p: forStartPage ? 0 : 3,
        borderRadius: forStartPage ? 4 : 2,
        maxWidth: "100%",
        margin: "0 auto",
        mb: 4,
      }}
    >
      {/* Add the visualizations */}

      {forStartPage && (
        <>
          <Box
            sx={{
              width: "100%",
              background: "linear-gradient(to bottom right, #ec4899, #f97316)",
              borderRadius: 4,
              p: 3,
              mb: 4,
              cursor: "pointer",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 10px 20px rgba(0,0,0,0.12)",
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                position: "relative",
                zIndex: 1,
              }}
            >
              <RocketLaunchIcon
                sx={{ color: "white", fontSize: 48, mb: 1.5 }}
              />
              <Box>
                <Typography
                  variant="h6"
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  Resultat denna månad
                </Typography>
                <Typography sx={{ color: "white", opacity: 0.8 }}>
                  Genomsnittligt resultat denna mån
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}

      <Box sx={{ p: forStartPage ? 3 : 0 }}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Total Assessments */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: theme.palette.background.paperHover,
                height: "100%",
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Totalt antal bedömningar
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                {stats.total}
              </Typography>
            </Paper>
          </Grid>

          {/* Average Score */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: theme.palette.background.paperHover,
                height: "100%",
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Genomsnittligt betyg
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                {(() => {
                  const grade = ["A", "B", "C", "D", "E", "F"][
                    Math.round(
                      Object.entries(stats.gradeDistribution).reduce(
                        (acc, [grade, count]) => {
                          const gradeValue = {
                            A: 0,
                            B: 1,
                            C: 2,
                            D: 3,
                            E: 4,
                            F: 5,
                          }[grade];
                          return acc + gradeValue * count;
                        },
                        0,
                      ) / stats.total,
                    )
                  ];
                  return (
                    <span style={{ color: gradeColors[grade] }}>{grade}</span>
                  );
                })()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {stats.averageScore}/5 poäng
              </Typography>
            </Paper>
          </Grid>

          {/* Top Grades (A) */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: theme.palette.background.paperHover,
                height: "100%",
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Antal högsta betyg (A)
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: "bold", color: gradeColors.A }}
              >
                {stats.gradeDistribution.A || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {round(
                  ((stats.gradeDistribution.A || 0) / stats.total) * 100,
                  1,
                )}
                % av total
              </Typography>
            </Paper>
          </Grid>

          {/* Failing Grades (F) */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: theme.palette.background.paperHover,
                height: "100%",
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Antal underkända (F)
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: "bold", color: gradeColors.F }}
              >
                {stats.gradeDistribution.F || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {round(
                  ((stats.gradeDistribution.F || 0) / stats.total) * 100,
                  1,
                )}
                % av total
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        <AssessmentVisualizations stats={stats} forStartPage={forStartPage} />

        {/* Grade Distribution */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            bgcolor: theme.palette.background.paperHover,
            mb: 4,
          }}
        >
          <Typography variant="h6" sx={{ mb: 3 }}>
            Betygsfördelning
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {Object.entries(gradeColors).map(([grade, color]) => (
              <Box key={grade}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ width: 20, fontWeight: "bold", fontSize: "1rem" }}
                  >
                    {grade}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 2 }}
                  >
                    {stats.gradeDistribution[grade] || 0} st
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={
                    ((stats.gradeDistribution[grade] || 0) / stats.total) * 100
                  }
                  sx={{
                    height: 20,
                    borderRadius: 4,
                    bgcolor: theme.palette.grey[200],
                    "& .MuiLinearProgress-bar": {
                      bgcolor: color,
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Criteria Stats */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            bgcolor: theme.palette.background.paperHover,
            display: forStartPage ? "none" : "block",
          }}
        >
          <Typography variant="h6" sx={{ mb: 3 }}>
            Resultat per bedömningskriterium
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {stats.criteriaStats.map((criteria) => (
              <Box key={criteria.name}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="body1" sx={{ flex: 1, fontWeight: 500 }}>
                    {criteria.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {criteria.averageScore}/5
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                  {Object.entries(gradeColors).map(([grade, color]) => (
                    <Tooltip
                      key={grade}
                      title={`${criteria.gradeDistribution[grade] || 0} st fick ${grade} på detta kriterium`}
                    >
                      <Box
                        sx={{
                          bgcolor: color,
                          flex: criteria.gradeDistribution[grade] || 0,
                          height: 20,
                          borderRadius: 3,
                          minWidth: 4,
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    </Paper>
  );
};

export default AssessmentStats;
